import Vue from 'vue';
// import { camelToSnakeCase, snakeToCamel } from '@/assets/utils/helpers';
import { APIHelper } from '@archistarai/auth-frontend';
import isObject from 'lodash/isObject';
import moment from 'moment';

export default {
  namespaced: true,
  state: {
    features: [],
    regions: [],
    duration: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 24, 36, 48, 60],
    paymentTerm: [
      {
        label: 'Monthly',
        value: 'monthly'
      },
      {
        label: 'Quarter',
        value: 'quarter'
      },
      {
        label: 'Half yearly',
        value: 'half yearly'
      },
      {
        label: 'Yearly',
        value: 'yearly'
      }
    ],
    discountType: ['$', '%'],
    subscriptions: [],
    chargebeeFeatureList: [],
    currentEditSubscription: null,
    archistarFeesPct: 1.5,
    marketingTags: [
      {
        label: 'Paused',
        value: 38
      },
      {
        label: 'Pause Period',
        value: 39
      },
      {
        label: 'Changed',
        value: 40
      },
      {
        label: 'Reseller',
        value: 35
      },
      {
        label: 'Roadshow',
        value: 33
      },
      {
        label: 'Self Checkout',
        value: 44
      }
    ]
  },
  getters: {
    getFeaturesForDropDown(state) {
      return (appId) =>
        state.features
          .filter((f) => f.appId === appId)
          .map((f) => ({
            label: f.label,
            id: f.id
          }));
    },
    getRegionForType(state) {
      return (type) =>
        state.regions
          .filter((f) => f.type === type)
          .map((f) => ({
            label: f.label,
            id: f.id
          }));
    },
    subscriptionWhereInCompany(state) {
      return (companies_guid) => state.subscriptions.filter((s) => companies_guid.includes(s.companyGuid));
    },
    subscriptionWhereCompany(state) {
      return (guid) => state.subscriptions.filter((s) => s.companyGuid === guid);
    },
    subscriptionWhereUser(state) {
      return (guid) => state.subscriptions.filter((s) => s.ownerGuid === guid);
    },
    subscriptionFind(state) {
      return (guid) => state.subscriptions.find((s) => s.guid === guid);
    }
  },
  mutations: {
    SET_CHARGEBEE_FEATURE_LIST(state, chargebeeFeatureList) {
      state.chargebeeFeatureList = chargebeeFeatureList;
    },
    SET_OBJECT(state, { key, value }) {
      state[key] = value;
    },
    ADD_FEATURES(state, features) {
      state.features = [...state.features, ...features];
    },
    ADD_SUBSCRIPTIONS(state, { subscriptions, force }) {
      if (force) state.subscriptions = subscriptions;
      subscriptions.forEach((subscription) => {
        const index = state.subscriptions.findIndex((s) => s.guid === subscription.guid);
        if (index >= 0) {
          // replace existing subscription
          Vue.set(state.subscriptions, index, subscription);
        } else {
          // not in list
          state.subscriptions.push(subscription);
        }
      });
    },
    ADD_SUBSCRIPTION(state, subscription) {
      const index = state.subscriptions.findIndex((s) => s.guid === subscription.guid);
      if (index >= 0) {
        // replace existing subscription
        Vue.set(state.subscriptions, index, subscription);
      } else {
        // not in list
        state.subscriptions.push(subscription);
      }
    },
    REMOVE_SUBSCRIPTION(state, guid) {
      const index = state.subscriptions.findIndex((s) => s.guid === guid);
      if (index >= 0) {
        state.subscriptions.splice(index, 1);
      }
    },
    SET_CURRENT_EDIT_SUBSCRIPTION(state, subscription) {
      if (!subscription) {
        state.currentEditSubscription = null;
        return;
      }
      let currentDate = moment();
      let nextChargeDate = moment.utc(subscription.next_charge);
      if (subscription.userSubscriptionsUpdate?.change_at) {
        subscription.changeAt = subscription.userSubscriptionsUpdate.change_at;
      } else {
        if (nextChargeDate.isSameOrAfter(currentDate)) {
          subscription.changeAt = nextChargeDate.toDate();
        } else {
          subscription.changeAt = currentDate.toDate();
        }
      }
      subscription.total =
        subscription.discountUnit == '%'
          ? subscription.priceExGst * ((100 - subscription.discountAmount) / 100)
          : subscription.priceExGst - subscription.discountAmount;
      subscription.quantity = 1;
      state.currentEditSubscription = subscription;
    }
  },
  actions: {
    async getRegions({ commit }) {
      try {
        const {
          data: { data }
        } = await APIHelper.AdminAPI.get('/app/subscription-regions');
        const regions = Object.keys(data).map((d) => ({
          id: d,
          label: data[d].name.toUpperCase(),
          type: data[d].type
        }));
        commit('SET_OBJECT', {
          key: 'regions',
          value: regions
        });

        return regions;
      } catch (e) {
        throw e;
      }
    },
    async getAllChargebeeFeatures({ commit }) {
      try {
        const {
          data: { data }
        } = await APIHelper.AdminAPI.get('/cb-products/all-features');
        data.sort((a, b) => a.name.localeCompare(b.name));
        commit('SET_CHARGEBEE_FEATURE_LIST', data);

        return data;
      } catch (e) {
        throw e;
      }
    },
    async getFeatures({ commit, state }, appId) {
      if (state.features.some((f) => f.appId === appId)) {
        //check if already have features
        return state.features.filter((f) => f.appId === appId);
      }
      try {
        const {
          data: { data }
        } = await APIHelper.AdminAPI.get(`/app/features/${appId}`);
        if (isObject(data) && !Array.isArray(data)) {
          const features = Object.keys(data).map((d) => ({
            id: d,
            label: data[d],
            appId: appId
          }));
          commit('ADD_FEATURES', features);
          return features;
        }
        return [];
      } catch (e) {
        throw e;
      }
    },
    async getSubscriptionsForCompany({ state, commit }, { guid, force = false }) {
      if (!force && state.subscriptions.some((s) => s.companyGuid === guid)) {
        //check if already have features
        return state.subscriptions.filter((s) => s.companyGuid === guid);
      }
      try {
        const {
          data: { data: subscriptions }
        } = await APIHelper.AdminAPI.get(`/user-subscriptions?scope=company&guid=${guid}`);
        commit('ADD_SUBSCRIPTIONS', { subscriptions, force });
        return subscriptions;
      } catch (e) {
        throw e;
      }
    },
    async getSubscriptionsForUser({ state, commit }, { guid, force = false }) {
      if (!force && state.subscriptions.some((s) => s.ownerGuid === guid)) {
        //check if already have features
        return state.subscriptions.filter((s) => s.ownerGuid === guid);
      }
      try {
        const {
          data: { data: subscriptions }
        } = await APIHelper.AdminAPI.get(`/user-subscriptions?scope=user&guid=${guid}`);
        commit('ADD_SUBSCRIPTIONS', { subscriptions, force });
        return subscriptions;
      } catch (e) {
        throw e;
      }
    },
    async getSubscriptionForUser({ commit }, guid) {
      try {
        const {
          data: { data: subscription }
        } = await APIHelper.AdminAPI.get(`/user-subscriptions/${guid}`);
        commit('ADD_SUBSCRIPTION', subscription);
        return subscription;
      } catch (e) {
        throw e;
      }
    },
    async deletePendingSubscription({ commit }, guid) {
      try {
        const {
          data: { data: deletedUserSubscriptionsGuid }
        } = await APIHelper.AdminAPI.delete(`/user-subscriptions/${guid}`);
        deletedUserSubscriptionsGuid.forEach((guid) => commit('REMOVE_SUBSCRIPTION', guid));
      } catch (e) {
        throw e;
      }
    }
  }
};
